/**
 * EG Search (Meilisearch) – infinite scroll (mobile).
 *
 * Uses an IntersectionObserver sentinel to load and append more hits.
 */
window.EGSearchResultsInfinite = (function () {
  'use strict';

  let observer = null;
  let state = {
    enabled: false,
    isLoading: false,
    done: false,
    nextPage: 1,
    nbPages: 0,
    loadedCount: 0,
    seenKeys: new Set(),
    resultsContainer: null,
    statsContainer: null,
    fetchPage: null,
    dedupe: null,
    appendHits: null,
    updateStats: null,
  };

  function teardown() {
    if (observer) {
      try { observer.disconnect(); } catch (e) { /* noop */ }
      observer = null;
    }
    if (state?.resultsContainer) {
      const footer = state.resultsContainer.querySelector('.meili-infinite-footer');
      if (footer) footer.remove();
    }
    state = {
      enabled: false,
      isLoading: false,
      done: false,
      nextPage: 1,
      nbPages: 0,
      loadedCount: 0,
      seenKeys: new Set(),
      resultsContainer: null,
      statsContainer: null,
      fetchPage: null,
      dedupe: null,
      appendHits: null,
      updateStats: null,
    };
  }

  function ensureFooter(container) {
    let footer = container.querySelector('.meili-infinite-footer');
    if (!footer) {
      footer = document.createElement('div');
      footer.className = 'meili-infinite-footer';
      footer.innerHTML = `
        <div class="loading-spinner meili-infinite-loading hidden"><div class="spinner"></div></div>
        <div class="meili-infinite-end hidden">No more results</div>
        <div class="meili-infinite-sentinel" aria-hidden="true"></div>
      `;
      container.appendChild(footer);
    }
    return footer;
  }

  async function loadMoreIfNeeded() {
    if (!state.enabled) return;
    if (state.isLoading || state.done) return;
    if (!state.fetchPage || !state.dedupe || !state.appendHits || !state.updateStats) return;
    const requestState = state;

    const container = state.resultsContainer;
    const statsContainer = state.statsContainer;
    if (!container) return;

    const footer = ensureFooter(container);
    const loadingEl = footer.querySelector('.meili-infinite-loading');
    const endEl = footer.querySelector('.meili-infinite-end');

    state.isLoading = true;
    if (loadingEl) loadingEl.classList.remove('hidden');
    if (endEl) endEl.classList.add('hidden');

    try {
      const pageToLoad = state.nextPage;
      const data = await state.fetchPage(pageToLoad);
      if (state !== requestState) return;
      const pageHits = state.dedupe(data.hits || []);

      const newHits = [];
      pageHits.forEach((hit) => {
        const key = hit && (hit.handle || hit.id || hit.objectID);
        if (!key) return;
        if (state.seenKeys.has(key)) return;
        state.seenKeys.add(key);
        newHits.push(hit);
      });

      if (newHits.length) {
        state.appendHits(newHits, container, state.loadedCount);
        state.loadedCount += newHits.length;
        state.updateStats({ ...data, page: 0 }, statsContainer, state.loadedCount);
      }

      state.nbPages = Number(data.nbPages) || state.nbPages;
      state.nextPage = pageToLoad + 1;
      if (state.nextPage >= state.nbPages) {
        state.done = true;
        if (endEl) endEl.classList.remove('hidden');
      }
    } catch (err) {
      if (state !== requestState) return;
      console.error('Infinite scroll load more error:', err);
      state.done = true;
    } finally {
      requestState.isLoading = false;
      if (loadingEl) loadingEl.classList.add('hidden');
    }
  }

  function setup({ resultsContainer, statsContainer, data, initialHits, fetchPage, dedupe, appendHits, updateStats }) {
    teardown();

    if (!resultsContainer) return;
    if (!('IntersectionObserver' in window)) return;

    const footer = ensureFooter(resultsContainer);
    const sentinel = footer.querySelector('.meili-infinite-sentinel');
    const endEl = footer.querySelector('.meili-infinite-end');
    const loadingEl = footer.querySelector('.meili-infinite-loading');

    if (loadingEl) loadingEl.classList.add('hidden');
    if (endEl) endEl.classList.add('hidden');

    state.enabled = true;
    state.resultsContainer = resultsContainer;
    state.statsContainer = statsContainer || null;
    state.fetchPage = fetchPage;
    state.dedupe = dedupe;
    state.appendHits = appendHits;
    state.updateStats = updateStats;

    state.nbPages = Number(data?.nbPages) || 0;
    state.nextPage = (Number(data?.page) || 0) + 1;
    state.loadedCount = (initialHits || []).length;
    state.seenKeys = new Set((initialHits || []).map(h => h && (h.handle || h.id || h.objectID)).filter(Boolean));
    state.done = state.nextPage >= state.nbPages;

    if (state.done) {
      if (endEl) endEl.classList.remove('hidden');
      return;
    }

    if (!sentinel) return;

    observer = new IntersectionObserver((entries) => {
      if (entries && entries.some(e => e.isIntersecting)) {
        loadMoreIfNeeded();
      }
    }, { root: null, rootMargin: '600px 0px', threshold: 0 });

    observer.observe(sentinel);
  }

  return {
    setup,
    teardown,
  };
})();
